import { ConsoleApiError } from "../api/errors";
import { t, type MessageId } from "../i18n";

/** Where the error happened: the same code can mean different things on each screen. */
export type ErrorContext = "signIn" | "verify" | "setPassword" | "general";

export type ErrorDisplay = {
  /** What failed and what to do next. */
  message: string;
  /** The request ID line, so staff can quote it (ADR-0016 §2.5). */
  reference: string;
};

const CONTEXT_OVERRIDES: Partial<Record<ErrorContext, Partial<Record<string, MessageId>>>> = {
  signIn: {
    unauthenticated: "error.signIn.unauthenticated",
    validation_error: "error.signIn.validation_error",
  },
  verify: {
    validation_error: "error.verify.validation_error",
    session_expired: "error.verify.session_expired",
    unauthenticated: "error.verify.unauthenticated",
  },
  // An unknown email and a wrong code read the same: the page must not reveal who is on the team.
  setPassword: {
    invalid_code: "error.setPassword.invalid_code",
    unauthenticated: "error.setPassword.invalid_code",
    too_many_attempts: "error.setPassword.too_many_attempts",
    validation_error: "error.setPassword.validation_error",
  },
};

const BY_CODE: Record<ConsoleApiError["code"], MessageId> = {
  unauthenticated: "error.unauthenticated",
  session_expired: "error.session_expired",
  feature_not_enabled: "error.feature_not_enabled",
  invalid_code: "error.invalid_code",
  too_many_attempts: "error.too_many_attempts",
  stale_version: "error.stale_version",
  validation_error: "error.validation_error",
  internal_error: "error.internal_error",
  network_error: "error.network_error",
  unexpected_response: "error.unexpected_response",
};

/**
 * What to say for a refusal the server named.
 *
 * A code says what kind of failure it was; a reason says what actually
 * happened, and that is the sentence somebody can act on. "This request has
 * already been decided" is a different thing to do about than "you cannot
 * decide your own time off", and both arrive as `validation_error`.
 *
 * The map grows as refusals are added. One that is not on it is not broken:
 * it falls through to the server's own sentence, and then to the code's.
 */
const BY_REASON: Partial<Record<string, MessageId>> = {
  // Everywhere
  stale_version: "error.stale_version",
  feature_not_enabled: "error.feature_not_enabled",
  permission_not_held: "error.reason.permission_not_held",
  not_found: "error.reason.not_found",
  no_reason: "error.reason.no_reason",

  // Teams and roles
  name_taken: "error.reason.name_taken",
  team_closed: "error.reason.team_closed",
  staff_deactivated: "error.reason.staff_deactivated",
  lead_must_be_member: "error.reason.lead_must_be_member",
  duplicate_change: "error.reason.duplicate_change",

  // Time off
  own_request: "error.reason.own_request",
  not_their_lead: "error.reason.not_their_lead",
  already_decided: "error.reason.already_decided",
  backwards: "error.reason.backwards",
  too_long: "error.reason.too_long",

  // Payroll
  month_open: "error.reason.month_open",
  already_paid: "error.reason.already_paid",
  already_closed: "error.reason.already_closed",
  nobody_payable: "error.reason.nobody_payable",
  invalid_month: "error.reason.invalid_month",

  // Performance
  own_review: "error.reason.own_review",
  not_the_reviewer: "error.reason.not_the_reviewer",
  signed_off: "error.reason.signed_off",
  no_outcome: "error.reason.no_outcome",

  // Recruiting and learning
  job_closed: "error.reason.job_closed",
  candidate_out: "error.reason.candidate_out",
  title_taken: "error.reason.title_taken",
  course_retired: "error.reason.course_retired",
  invalid_progress: "error.reason.invalid_progress",
};

/**
 * Reads an error the way the whole console reads one.
 *
 * The reason first, when it is one we have words for; then the code's message.
 *
 * Never the server's own sentence, however good it is. The server writes in
 * English and this console is read in eight languages, so showing it would put
 * English in front of somebody who chose French — and the message that says
 * exactly the right thing in a language you do not read says nothing.
 *
 * That is what the reason is for: the server names what happened, and the name
 * is translated here. A reason with no words yet falls back to the code's
 * message, which is vaguer but always translated. The map grows.
 */
export function describeError(error: unknown, context: ErrorContext = "general"): ErrorDisplay {
  if (!(error instanceof ConsoleApiError)) {
    return { message: t("error.unknown"), reference: t("error.noRequestId") };
  }
  const reference = error.requestId ? t("error.requestId", { id: error.requestId }) : t("error.noRequestId");

  const override = CONTEXT_OVERRIDES[context]?.[error.code];
  if (override) return { message: t(override), reference };

  if (error.code === "network_error") {
    return { message: t(error.outcomeUnknown ? "error.network_error.write" : "error.network_error"), reference };
  }

  const known = error.reason ? BY_REASON[error.reason] : undefined;
  if (known) return { message: t(known), reference };

  return { message: t(BY_CODE[error.code]), reference };
}
